import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import classNames from 'classnames';
import useChat from './hooks/useChat';
import { useAIAuth } from '../hooks/useAIAuth';
import ChatMessage from './components/ChatMessage';
import ChatInput from './components/ChatInput';
import TodosSection from './components/TodosSection';
import ChatEmptyState from './components/ChatEmptyState';
import PreviewPanel from './components/PreviewPanel';
import SessionList from './components/SessionList';
import Icon, { IconSizes, IconVariants } from '../components/foundations/Icon';
import { getChatEndpoint } from './service';
import { MESSAGE_ROLES } from './constants';
import css from './page.css';
import { Button } from '../components/widgets';

const AnalysisChat = () => {
    const messagesEndRef = useRef(null);
    const [showSessionSidebar, setShowSessionSidebar] = useState(true);
    const [dashboardArtifacts, setDashboardArtifacts] = useState(null);

    const { token, isLoading: authLoading, error: authError } = useAIAuth();

    const {
        messages,
        input,
        handleInputChange,
        handleSubmit,
        isLoading,
        error,
        stop,
        reload,
        currentSessionId,
        sessions,
        isLoadingSessions,
        clearCurrentSession,
        loadSession,
        deleteSession,
        updateSessionTitle,
    } = useChat({
        api: getChatEndpoint('ai/analytics'),
        token,
    });

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const onMicClick = useCallback(() => {
        // TODO: Implement microphone functionality
    }, []);

    const hasMessages = messages.length > 0;

    // Find the currently active assistant message with steps
    const activeSteps = useMemo(() => {
        const activeAssistantMessage = [...messages]
            .reverse()
            .find(msg => msg.role === MESSAGE_ROLES.ASSISTANT && msg.steps && msg.steps.length > 0);
        return activeAssistantMessage?.steps || [];
    }, [messages]);

    const lastMessageDashboardArtifacts = useMemo(() => {
        const lastMessage = messages?.[messages.length - 1];

        if (!lastMessage || lastMessage.role !== MESSAGE_ROLES.ASSISTANT) return null;

        return lastMessage.content?.filter(block => block.is_final_analysis);
    }, [messages]);

    const onDashboardClick = artifacts => setDashboardArtifacts(artifacts);

    const previewArtifacts = dashboardArtifacts?.length > 0 ? dashboardArtifacts : lastMessageDashboardArtifacts;

    if (authLoading) {
        return (
            <div className={css.chatPage}>
                <div className={css.chatPageLoading}>
                    <Icon name="loader" size={IconSizes.LARGE} className={css.spinningLoader} />
                    <div className={css.chatPageLoadingText}>Authenticating...</div>
                </div>
            </div>
        );
    }

    if (authError) {
        return (
            <div className={css.chatPage}>
                <div className={css.chatPageError}>
                    <Icon name="alert-triangle" size={IconSizes.LARGE} className={css.chatErrorIcon} />
                    <div className={css.chatPageErrorText}>Authentication failed: {authError}</div>
                </div>
            </div>
        );
    }

    const onNewChat = () => {
        clearCurrentSession();
        setDashboardArtifacts(null);
    };

    const toggleSessionSidebar = () => setShowSessionSidebar(!showSessionSidebar);

    const onSessionDelete = async sessionId => await deleteSession(sessionId);

    const onSessionSelect = async sessionId => {
        if (sessionId !== currentSessionId) {
            setDashboardArtifacts(null);
            await loadSession(sessionId);
        }
    };

    const onSessionUpdate = async (sessionId, updates) => {
        if (updates.title) await updateSessionTitle(sessionId, updates.title);
    };

    return (
        <div className={css.chatPage}>
            <div className={css.chatPageHeader}>
                <div className={css.chatPageTitle}>Analysis Chat</div>
                <Button Icon="plus" onClick={onNewChat} disabled={isLoading}>
                    New Chat
                </Button>
            </div>

            <div className={css.chatPageBody}>
                <div className={classNames(css.sessionSidebar, { [css.collapsed]: !showSessionSidebar })}>
                    <SessionList
                        sessions={sessions}
                        currentSessionId={currentSessionId}
                        onSessionSelect={onSessionSelect}
                        onSessionDelete={onSessionDelete}
                        onSessionUpdate={onSessionUpdate}
                        isLoading={isLoadingSessions}
                    />
                </div>
                <Icon
                    name={showSessionSidebar ? 'chevron-left' : 'chevron-right'}
                    size={IconSizes.MEDIUM}
                    className={classNames(css.sidebarButton, { [css.collapsed]: !showSessionSidebar })}
                    onClick={toggleSessionSidebar}
                    hoverText={showSessionSidebar ? 'Collapse sidebar' : 'Expand sidebar'}
                />
                <div className={css.chatPageContent}>
                    <div className={css.chatMessagesContainer}>
                        {!hasMessages && (
                            <ChatEmptyState
                                onSuggestionClick={query => handleSubmit(null, { content: query })}
                                input={input}
                                handleInputChange={handleInputChange}
                                handleSubmit={handleSubmit}
                                isLoading={isLoading}
                                stop={stop}
                                onMicClick={onMicClick}
                            />
                        )}
                        {hasMessages && (
                            <div className={css.chatMessagesList}>
                                {messages.map(message => (
                                    <ChatMessage
                                        key={message.id}
                                        message={message}
                                        onDashboardClick={onDashboardClick}
                                    />
                                ))}
                                <div ref={messagesEndRef} />
                            </div>
                        )}
                        {error && (
                            <div className={css.chatError}>
                                <Icon
                                    name="alert-triangle"
                                    variant={IconVariants.ERROR}
                                    size={IconSizes.SMALL}
                                    className={css.chatErrorIcon}
                                    interactive={false}
                                />
                                <span className={css.chatErrorMessage}>{error}</span>
                                <button onClick={reload} className={css.chatErrorRetry}>
                                    Retry
                                </button>
                            </div>
                        )}
                    </div>
                    {activeSteps.length > 0 && <TodosSection steps={activeSteps} />}
                    {hasMessages && (
                        <ChatInput
                            input={input}
                            handleInputChange={handleInputChange}
                            handleSubmit={handleSubmit}
                            isLoading={isLoading}
                            stop={stop}
                            onMicClick={onMicClick}
                        />
                    )}
                </div>
                {previewArtifacts?.length > 0 && <PreviewPanel key={currentSessionId} artifacts={previewArtifacts} />}
            </div>
        </div>
    );
};

export default AnalysisChat;
